import React, { useMemo, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'

import SyncView from '../SyncView'
import { tokens } from '../../theme'
import { deriveSyncStatusLine } from '../../utils/syncStatusLine'
import SyncPanelDialog from './SyncPanel'
import { useSyncStatus } from './useSyncStatus'
import { useNow } from './useSyncPanelModel'

// Relógio do "há X min", compartilhado com o painel (QA HML U14).
export const SYNC_CLOCK_TICK_MS = 15000

const TONE_COLOR = {
  pending: tokens.semantic.warning.main,
  ok: tokens.semantic.success.main,
  refused: tokens.semantic.error.main,
  neutral: '#5C5C5C',
}

export default function SyncIndicator({
  compact = false,
  useStatus = useSyncStatus,
}) {
  const currentSync = useSelector((s) => s.sync.currentSync)
  const { snapshot } = useStatus(currentSync)
  const [open, setOpen] = useState(false)
  const now = useNow(SYNC_CLOCK_TICK_MS, snapshot)

  const line = useMemo(
    () =>
      snapshot
        ? deriveSyncStatusLine({
            now,
            online: snapshot.online,
            summary: snapshot.summary,
            lastPushOkAt: snapshot.lastPushOkAt,
          })
        : null,
    [snapshot, now],
  )

  const color = line ? TONE_COLOR[line.tone] : undefined
  const showBadge = line && (compact || line.count != null)

  return (
    <Box
      data-testid='sync-indicator'
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    >
      {/* O clique no ícone (inclusive o <button> interno do SyncView em erro) borbulha até aqui. */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          position: 'relative',
          display: 'inline-block',
          cursor: 'pointer',
        }}
      >
        <SyncView tone={line?.tone} />
        {showBadge && (
          <Box
            component='span'
            data-testid='sync-badge'
            aria-label={line.detail}
            title={line.detail}
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              px: 0.5,
              borderRadius: '9px',
              bgcolor: color,
              color: '#fff',
              border: '2px solid #fff',
              fontSize: '0.6875rem',
              fontWeight: 700,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {line.badge}
          </Box>
        )}
      </Box>
      {line && !compact && (
        <Tooltip title={line.detail} arrow>
          <Box
            component='span'
            role='status'
            aria-live='polite'
            sx={{ display: 'inline-flex' }}
          >
            <Box
              component='button'
              type='button'
              data-testid='sync-status-line'
              aria-haspopup='dialog'
              onClick={() => setOpen(true)}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                color,
                fontSize: '0.8125rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                maxWidth: 240,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:focus-visible': {
                  outline: '2px solid currentColor',
                  outlineOffset: 2,
                },
              }}
            >
              {line.text}
            </Box>
          </Box>
        </Tooltip>
      )}
      <SyncPanelDialog
        open={open}
        onClose={() => setOpen(false)}
        snapshot={snapshot}
        now={now}
      />
    </Box>
  )
}
